class HeroSliderElement extends HTMLElement {
  static DEFAULT_IMAGE_DELAY = 10000;

  connectedCallback() {
    this.swiperEl = this.querySelector('.hero-swiper');
    this.slides = [...this.querySelectorAll('.hero-swiper .swiper-slide')];
    this.paginationItems = [...this.querySelectorAll('.hero-pagination-item')];
    this.paginationFills = this.paginationItems.map(item => item.querySelector('.hero-pagination-fill'));
    this.imageDelay = parseInt(this.dataset.autoplayDelay, 10) || HeroSliderElement.DEFAULT_IMAGE_DELAY;
    this.currentIndex = 0;
    this.imageTimer = null;
    this.swiper = null;
    this._currentVideo = null;
    this._paginationClickHandlers = [];
    this._slideClickHandlers = [];
    this._slideKeyHandlers = [];
    this._boundOnVideoTimeUpdate = this._onVideoTimeUpdate.bind(this);
    this._boundOnVideoEnded = this._onVideoEnded.bind(this);

    this._init();
  }

  disconnectedCallback() {
    this._destroy();
  }

  _init() {
    if (!this.swiperEl || this.slides.length === 0) return;

    this.swiper = new Swiper(this.swiperEl, {
      loop: true,
      speed: 500,
      allowTouchMove: true,
      autoplay: false,
      pagination: false,
    });

    this.swiper.on('slideChangeTransitionStart', (swiper) => {
      this._activateSlide(swiper.realIndex);
    });

    this.paginationItems.forEach((item, i) => {
      const handler = () => {
        if (i === this.currentIndex) return;
        this._stopCurrentSlide();
        this.swiper.slideToLoop(i);
      };
      item.addEventListener('click', handler);
      this._paginationClickHandlers.push(handler);
    });

    this.slides.forEach((slide, i) => {
      const link = slide.dataset.slideLink;
      if (!link) return;

      const clickHandler = (e) => {
        if (e.target.closest('a, button, .slideshow-info')) return;
        window.location.href = link;
      };
      const keyHandler = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          window.location.href = link;
        }
      };

      slide.addEventListener('click', clickHandler);
      slide.addEventListener('keydown', keyHandler);
      this._slideClickHandlers[i] = clickHandler;
      this._slideKeyHandlers[i] = keyHandler;
    });

    this._activateSlide(0);
  }

  _activateSlide(index) {
    this._stopCurrentSlide();
    this.currentIndex = index;
    this._updatePagination(index);

    const slide = this.slides[index];
    const video = this._getVisibleVideo(slide);

    if (video) {
      this._startVideoSlide(video);
    } else {
      this._startImageSlide();
    }
  }

  _startVideoSlide(video) {
    this._currentVideo = video;
    video.currentTime = 0;
    video.play().catch(() => {});
    video.addEventListener('timeupdate', this._boundOnVideoTimeUpdate);
    video.addEventListener('ended', this._boundOnVideoEnded);
  }

  _startImageSlide() {
    const fill = this.paginationFills[this.currentIndex];
    const item = this.paginationItems[this.currentIndex];

    if (item && fill) {
      item.style.setProperty('--bar-duration', `${this.imageDelay / 1000}s`);
      fill.classList.remove('is-animating');
      void fill.offsetWidth;
      fill.classList.add('is-animating');
    }

    this.imageTimer = setTimeout(() => this._advance(), this.imageDelay);
  }

  _onVideoTimeUpdate() {
    const video = this._currentVideo;
    if (!video?.duration) return;

    const fill = this.paginationFills[this.currentIndex];
    if (fill) {
      fill.style.setProperty('--fill-progress', `${(video.currentTime / video.duration) * 100}%`);
    }
  }

  _onVideoEnded() {
    this._advance();
  }

  _advance() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this._stopCurrentSlide();
    this.swiper.slideToLoop(nextIndex);
  }

  _stopCurrentSlide() {
    clearTimeout(this.imageTimer);
    this.imageTimer = null;

    if (this._currentVideo) {
      this._currentVideo.pause();
      this._currentVideo.currentTime = 0;
      this._currentVideo.removeEventListener('timeupdate', this._boundOnVideoTimeUpdate);
      this._currentVideo.removeEventListener('ended', this._boundOnVideoEnded);
      this._currentVideo = null;
    }
  }

  _getVisibleVideo(slide) {
    const selector = window.innerWidth < 768 ? '.hero-video--mobile' : '.hero-video--desktop';
    const video = slide.querySelector(selector);
    return video?.querySelector('source') ? video : null;
  }

  _updatePagination(activeIndex) {
    this.paginationItems.forEach((item, i) => {
      const fill = this.paginationFills[i];
      if (fill) {
        fill.classList.remove('is-animating');
        fill.style.removeProperty('--fill-progress');
      }
      item.classList.toggle('is-active', i === activeIndex);
    });
  }

  _destroy() {
    this._stopCurrentSlide();
    this.paginationItems.forEach((item, i) => {
      if (this._paginationClickHandlers[i]) {
        item.removeEventListener('click', this._paginationClickHandlers[i]);
      }
    });
    this._paginationClickHandlers = [];
    this.slides.forEach((slide, i) => {
      if (this._slideClickHandlers[i]) slide.removeEventListener('click', this._slideClickHandlers[i]);
      if (this._slideKeyHandlers[i]) slide.removeEventListener('keydown', this._slideKeyHandlers[i]);
    });
    this._slideClickHandlers = [];
    this._slideKeyHandlers = [];
    this.swiper?.destroy();
  }
}

if (!customElements.get('hero-slider-element')) {
  customElements.define('hero-slider-element', HeroSliderElement);
}
